import { Box, List, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '../common/modals/confirmationModal';
import {
  AffiliationRelations,
  AffiliationTypes,
  BinaryAnswer,
  binaryAnswers,
} from './resources';
import OvForm from '../common/wrappers/ovForm';
import ButtonListItem from '../common/lists/buttonListItem';
import { userIdVar } from '../../utils/localVariables';
import { Affiliation } from './createAccountAuxiliaryWizard';
import { sendAnalyticsEvent, ovAnalyticsEvents } from '../../utils/firebase';

interface Props {
  isRespFamily?: boolean,
  onContinue?: () => void,
  setCaregiverAffiliation: (att: Affiliation) => void,
}

const BeneficiaryCaregiver = ({ isRespFamily, onContinue, setCaregiverAffiliation }: Props): JSX.Element => {
  const { t } = useTranslation(['account']);
  const [displayConfirmationMessage, setDisplayConfirmationMessage] = useState(false);

  const dialogClose = (): void => {
    setDisplayConfirmationMessage(false);
  };

  const updateCareGiverAffiliation = (): void => {
    setCaregiverAffiliation({
      userId: userIdVar(),
      type: AffiliationTypes.OTHER,
      relation: AffiliationRelations.PRIMARY_CAREGIVER,
      signatureRequired: false,
    });
    if (onContinue) onContinue();
  };

  const handleClick = async (item: Record<string, string>): Promise<void> => {
    if (item.key === BinaryAnswer.YES) {
      sendAnalyticsEvent(ovAnalyticsEvents.addBeneficiaryPrimaryCaregiverYesButtonSelect).then();
      updateCareGiverAffiliation();
    } else {
      sendAnalyticsEvent(ovAnalyticsEvents.addBeneficiaryPrimaryCaregiverNoButtonSelect).then();
      setDisplayConfirmationMessage(true);
    }
  };

  return (
    <OvForm hasButton={false} loading={false}>
      <Typography variant="heading2" data-testid="title">{t(`beneficiary.caregiver.${isRespFamily ? 'title2' : 'title'}`)}</Typography>
      <Typography variant="paragraph3" whiteSpace="pre-wrap" style={{ marginBottom: 32 }}>{t('beneficiary.caregiver.subtitle')}</Typography>
      <List sx={{ maxHeight: '250px', overflowY: 'auto' }}>
        {binaryAnswers.map((item: Record<string, string>) => (
          <Box key={item.key}>
            <ButtonListItem
              key={item.key}
              text={item.value}
              onClick={() => handleClick(item)}
            />
            <Box mb={1} />
          </Box>
        ))}
      </List>
      <ConfirmationModal
        title={t('beneficiary.support.title')}
        description={t('beneficiary.support.subtitle', { relationship: t('beneficiary.support.primaryCaregiver') })}
        buttonText={t('beneficiary.support.understand')}
        open={displayConfirmationMessage}
        onButtonClick={dialogClose}
        onClose={dialogClose}
      />
    </OvForm>
  );
};

BeneficiaryCaregiver.defaultProps = {
  isRespFamily: false,
  onContinue: undefined,
};

export default BeneficiaryCaregiver;
